import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { BiDownload } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import { downloadWorkbook } from '../../utils/downloadWorkbook';
import { useWorkspaceAccess } from '../../hooks/useWorkspaceAccess';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspaceModal from '../../components/workspace/WorkspaceModal';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './PlatformAuditPage.scss';

function isoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function thisMonth() {
  const now = new Date();
  return {
    from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  };
}

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const PlatformAuditPage = () => {
  const { allowed } = useWorkspaceAccess();
  const initial = thisMonth();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  useDocumentTitle('Audit trail');

  const load = async (range) => {
    setLoading(true);
    setError('');
    try {
      const response = await platformAPI.listAudit({ page: 1, limit: 500, from: range.from, to: range.to });
      setLogs(response.data?.data?.logs || []);
      setTotal(response.data?.data?.total ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load the audit trail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(applied);
  }, [applied]);

  const apply = (event) => {
    event.preventDefault();
    if (from && to && from > to) {
      setError('The start date needs to be on or before the end date.');
      return;
    }
    setApplied({ from, to });
  };

  const reset = () => {
    const range = thisMonth();
    setFrom(range.from);
    setTo(range.to);
    setApplied(range);
  };

  const rows = useMemo(() => logs.map((row) => ({
    ...row,
    whenLabel: formatWhen(row.timestamp || row.createdAt),
    departmentLabel: row.department || '—',
    ipLabel: row.ipAddress || '—',
    descriptionLabel: row.description || '—'
  })), [logs]);

  const columns = useMemo(() => [
    { key: 'userName', label: 'User', mobileTitle: true },
    { key: 'departmentLabel', label: 'Department' },
    { key: 'whenLabel', label: 'Date' },
    { key: 'actionLabel', label: 'Action' },
    { key: 'screen', label: 'Screen' },
    { key: 'descriptionLabel', label: 'Description' },
    {
      key: 'ipLabel',
      label: 'Address',
      render: (value) => <span className="audit-report__ip">{value}</span>
    },
    {
      key: 'outcome',
      label: 'Status',
      render: (value) => (
        <span className={`audit-report__status${value === 'Failed' ? ' is-failed' : ''}`}>{value}</span>
      )
    }
  ], []);

  const exportExcel = () => {
    downloadWorkbook('audit-trail', rows.map((row) => ({
      User: row.userName || '',
      Department: row.departmentLabel === '—' ? '' : row.departmentLabel,
      Date: row.whenLabel === '—' ? '' : row.whenLabel,
      Action: row.actionLabel || '',
      Screen: row.screen || '',
      Description: row.descriptionLabel === '—' ? '' : row.descriptionLabel,
      Address: row.ipLabel === '—' ? '' : row.ipLabel,
      Status: row.outcome || ''
    })));
  };

  return (
    <div className="audit-report activity-log">
      <WorkspacePageHeader
        title="Audit trail"
        subtitle="Saves, edits, deletes, and sign-ins for the dates you choose. Select a row to read the detail."
        actions={(
          <Button variant="outline-primary" size="sm" onClick={exportExcel} disabled={loading || !logs.length || !allowed(['audit.download', 'platform.audit.read'])}>
            <BiDownload className="me-1" />
            Export Excel
          </Button>
        )}
      />

      <form className="audit-report__range" onSubmit={apply}>
        <label>
          From
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} required />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} required />
        </label>
        <div className="audit-report__range-actions">
          <Button type="submit" size="sm" variant="primary">Apply</Button>
          <Button type="button" size="sm" variant="outline-primary" onClick={reset}>This month</Button>
        </div>
        <span className="audit-report__count">
          {loading ? 'Loading…' : `${total} event${total === 1 ? '' : 's'}`}
        </span>
      </form>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>
      ) : (
        <WorkspaceDataTable
          className="activity-log__table"
          title="Events"
          data={rows}
          columns={columns}
          searchPlaceholder="Search user, screen, or description…"
          showActions={false}
          showCheckboxes={false}
          exportable={false}
          pageSize={12}
          emptyMessage="No events in this period"
          onRowClick={(row) => setDetail(row)}
        />
      )}

      <WorkspaceModal
        show={Boolean(detail)}
        onHide={() => setDetail(null)}
        title={detail ? `${detail.actionLabel || 'Event'} · ${detail.screen || ''}` : 'Event'}
        description={detail ? formatWhen(detail.timestamp || detail.createdAt) : ''}
        size="lg"
        footer={(
          <Button variant="outline-secondary" onClick={() => setDetail(null)}>Close</Button>
        )}
      >
        {detail ? (
          <dl className="activity-log__detail">
            <div className="activity-log__detail-row">
              <dt>User</dt>
              <dd>{detail.userName}{detail.userEmail ? ` (${detail.userEmail})` : ''}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Department</dt>
              <dd>{detail.department || '—'}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Action</dt>
              <dd>{detail.actionLabel}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Screen</dt>
              <dd>{detail.screen || '—'}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Description</dt>
              <dd>{detail.description || '—'}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Address</dt>
              <dd>{detail.ipAddress || '—'}</dd>
            </div>
            <div className="activity-log__detail-row">
              <dt>Status</dt>
              <dd>{detail.outcome || detail.status || 'Success'}</dd>
            </div>
          </dl>
        ) : null}
      </WorkspaceModal>
    </div>
  );
};

export default PlatformAuditPage;
